import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
    Layers, School, PlusCircle, X, CheckCircle2, AlertTriangle, Trash2, Edit3, ShieldCheck, ChevronDown, ChevronRight, Save, BookOpen 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import { getUserPermissions } from '../../utils/permissions_utils';
import API, { socket } from '../../services/api';

const ClasseNiveau = ({ user }) => {
    const navigate = useNavigate();
    const userPerms = useMemo(() => getUserPermissions(), []);
    
    // Onglet actif : 'niveaux' ou 'classes'
    const [activeTab, setActiveTab] = useState('niveaux');
    // États communs
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [confirmAction, setConfirmAction] = useState({ show: false, id: null, type: null, message: '' });
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    
    // Données métiers
    const [niveaux, setNiveaux] = useState([]);
    const [classes, setClasses] = useState([]);
    const [cycles, setCycles] = useState([]);
    const [annees, setAnnees] = useState([]);
    const [enseignants, setEnseignants] = useState([]);
    const [matieresCatalogue, setMatieresCatalogue] = useState([]); // 👈 Catalogue des matières
    const [decoupages, setDecoupages] = useState([]); // 👈 Types de découpage
    
    // Filtre des classes par année scolaire
    const [selectedAnneeId, setSelectedAnneeId] = useState('');
    
    // Gestion des accordéons et données des 2 volets par classe
    const [classesOuvertes, setClassesOuvertes] = useState({});
    const [matieresParClasse, setMatieresParClasse] = useState({});

    // États locaux pour le volet 1 de la classe ouverte
    const [selectedMatiereCatalog, setSelectedMatiereCatalog] = useState(null);
    const [tempCoefficient, setTempCoefficient] = useState(1);
    const [lignesMatieresClasse, setLignesMatieresClasse] = useState([]);

    // États locaux pour le volet 2 de la classe ouverte
    const [selectedProf, setSelectedProf] = useState(null);
    const [specialitesProfCochees, setSpecialitesProfCochees] = useState({});

    // Formulaires séparés
    const [niveauForm, setNiveauForm] = useState({
        nom_niveau: '',
        cycle_id: '',
        ordre: 1,
        est_classe_examen: 0
    });
    const [classeForm, setClasseForm] = useState({
        niveau_id: '',
        annee_scolaire_id: '',
        nom_classe: '',
        code_classe: '',
        capacite: 35,
        salle: '',
        titulaire_enseignant_id: '',
        est_classe_examen: 0,
        type_decoupage_id: '' // 👈 Ajout du type de découpage
    });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 5000);
    };

    // --- CHARGEMENT DES DONNÉES ---
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [resCycles, resAnnees, resEns, resMat, resDec] = await Promise.all([
                API.get('/scolarite/cycles/liste'),
                API.get('/scolarite/annees/liste'),
                API.get('/scolarite/enseignants/liste').catch(() => ({ data: { success: false, data: [] } })),
                API.get('/scolarite/matieres/liste').catch(() => ({ data: { success: false, data: [] } })),
                API.get('/scolarite/decoupages/liste').catch(() => ({ data: { success: false, data: [] } }))
            ]);
            if (resCycles.data.success) setCycles(resCycles.data.data);
            if (resEns.data.success) setEnseignants(resEns.data.data);
            if (resMat.data.success) setMatieresCatalogue(resMat.data.data);
            if (resDec.data.success) setDecoupages(resDec.data.data);

            let anneeIdActive = selectedAnneeId;
            if (resAnnees.data.success) {
                const listeAnnees = resAnnees.data.data;
                setAnnees(listeAnnees);
                const ouverte = listeAnnees.find(a => a.statut === 'OUVERT') || listeAnnees[0];
                if (ouverte && !selectedAnneeId) {
                    anneeIdActive = ouverte.id;
                    setSelectedAnneeId(ouverte.id);
                }
            }

            if (activeTab === 'niveaux') {
                const resNiv = await API.get('/scolarite/niveaux/liste');
                if (resNiv.data.success) setNiveaux(resNiv.data.data);
            } else {
                const url = anneeIdActive ? `/scolarite/classes/liste?annee_scolaire_id=${anneeIdActive}` : '/scolarite/classes/liste';
                const resCls = await API.get(url);
                if (resCls.data.success) {
                    const classesList = resCls.data.data;
                    setClasses(classesList);

                    // Charger les matières et affectations pour chaque classe
                    if (anneeIdActive) {
                        const regroupement = {};
                        for (const cls of classesList) {
                            try {
                                const resAff = await API.get(`/scolarite/matieres/classe/${cls.id}?annee_scolaire_id=${anneeIdActive}`);
                                regroupement[cls.id] = resAff.data.success ? resAff.data.data : [];
                            } catch (e) {
                                regroupement[cls.id] = [];
                            }
                        }
                        setMatieresParClasse(regroupement);
                    }
                }
            }
        } catch (err) {
            showToast("Erreur lors du chargement des données", "error");
        } finally {
            setLoading(false);
        }
    }, [activeTab, selectedAnneeId]);

    useEffect(() => {
        fetchData();
        if (socket && user?.company_id) {
            const room = String(user.company_id);
            socket.emit('join_company', room);
            socket.on('DATA_EVENT', (event) => {
                if (['niveaux', 'classes', 'cycles', 'annees_scolaires', 'users', 'matieres', 'classe_matieres', 'types_decoupage'].includes(event.table)) {
                    fetchData();
                }
            });
        }
        return () => {
            if (socket) socket.off('DATA_EVENT');
        };
    }, [fetchData, user?.company_id]);

    const resetForms = () => {
        setShowForm(false);
        setEditingId(null);
        setNiveauForm({ nom_niveau: '', cycle_id: '', ordre: 1, est_classe_examen: 0 });
        setClasseForm({ niveau_id: '', annee_scolaire_id: selectedAnneeId || '', nom_classe: '', code_classe: '', capacite: 35, salle: '', titulaire_enseignant_id: '', est_classe_examen: 0, type_decoupage_id: '' });
    };

    // --- GESTION DES ACCORDÉONS PAR CLASSE ---
    const toggleAccordion = (classeId) => {
        const isOpen = !!classesOuvertes[classeId];
        setClassesOuvertes(prev => ({ ...prev, [classeId]: !isOpen }));
        if (!isOpen) {
            const existantes = matieresParClasse[classeId] || [];
            setLignesMatieresClasse(existantes.map(m => ({
                matiere_id: m.matiere_id,
                libelle: m.libelle,
                code_matiere: m.code_matiere,
                coefficient: m.coefficient || 1,
                enseignant_id: m.enseignant_id || null
            })));
            setSelectedMatiereCatalog(null);
            setSelectedProf(null);
            setSpecialitesProfCochees({});
        }
    };

    // --- VOLET 1 : Ajouter une matière au panier de la classe ---
    const handleAddMatiereToClass = () => {
        if (!selectedMatiereCatalog) return showToast("Veuillez sélectionner une matière", "error");
        if (lignesMatieresClasse.some(l => l.matiere_id === selectedMatiereCatalog.id)) {
            return showToast("Cette matière est déjà configurée pour cette classe", "error");
        }

        const nouvelleLigne = {
            matiere_id: selectedMatiereCatalog.id,
            libelle: selectedMatiereCatalog.libelle,
            code_matiere: selectedMatiereCatalog.code_matiere,
            coefficient: parseFloat(tempCoefficient) || 1,
            enseignant_id: null
        };

        setLignesMatieresClasse([...lignesMatieresClasse, nouvelleLigne]);
        setSelectedMatiereCatalog(null);
        setTempCoefficient(1);
    };

    // --- VOLET 2 : Sélection rigoureuse du professeur basée sur ses spécialités ---
    const handleSelectProfesseur = (prof) => {
        if (!prof) {
            setSelectedProf(null);
            setSpecialitesProfCochees({});
            return;
        }

        const matieresClasseIds = lignesMatieresClasse.map(l => l.matiere_id);
        const profSpecs = prof.specialites || [];
        
        const hasValidSpecialty = profSpecs.some(s => matieresClasseIds.includes(s.id || s.matiere_id));

        if (!hasValidSpecialty) {
            showToast(`Refusé : ${prof.username} ne dispose d'aucun rattachement de spécialité parmi les matières de cette classe.`, "error");
            setSelectedProf(null);
            setSpecialitesProfCochees({});
            return;
        }

        setSelectedProf(prof);
        const initialCoches = {};
        lignesMatieresClasse.forEach(l => {
            if (l.enseignant_id === prof.id && profSpecs.some(s => (s.id || s.matiere_id) === l.matiere_id)) {
                initialCoches[l.matiere_id] = true;
            }
        });
        setSpecialitesProfCochees(initialCoches);
    };

    const handleToggleSpecialiteCoche = (matiereId) => {
        setSpecialitesProfCochees(prev => ({
            ...prev,
            [matiereId]: !prev[matiereId]
        }));
    };

    const handleApplyProfesseurToClass = () => {
        if (!selectedProf) return showToast("Veuillez sélectionner un professeur", "error");

        const majLignes = lignesMatieresClasse.map(l => {
            const estCoche = specialitesProfCochees[l.matiere_id];
            const appartientAuxSpecs = selectedProf.specialites?.some(s => (s.id || s.matiere_id) === l.matiere_id);

            if (estCoche && appartientAuxSpecs) {
                return { ...l, enseignant_id: selectedProf.id };
            } else if (l.enseignant_id === selectedProf.id && !estCoche) {
                return { ...l, enseignant_id: null };
            }
            return l;
        });

        setLignesMatieresClasse(majLignes);
        showToast(`Affectations mises à jour pour ${selectedProf.username}`);
    };

    // --- ENREGISTREMENT GLOBAL DE LA CONFIGURATION DE LA CLASSE ---
    const handleSaveConfiguration = async (classeId) => {
        if (!selectedAnneeId) return showToast("Veuillez sélectionner une année scolaire", "error");

        setActionLoading(true);
        try {
            const payload = {
                classe_id: classeId,
                annee_scolaire_id: selectedAnneeId,
                lignes: lignesMatieresClasse.map(l => ({
                    matiere_id: l.matiere_id,
                    enseignant_id: l.enseignant_id || null,
                    coefficient: parseFloat(l.coefficient) || 1
                }))
            };

            await API.post('/scolarite/matieres/affecter', payload);
            showToast("Configuration de la classe enregistrée avec succès.");
            fetchData();
        } catch (err) {
            showToast(err.response?.data?.error || "Erreur lors de l'enregistrement", "error");
        } finally {
            setActionLoading(false);
        }
    };

    // --- GESTION DES SOUMISSIONS (NIVEAUX / CLASSES) ---
    const handleNiveauSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            if (editingId) {
                await API.put(`/scolarite/niveaux/${editingId}`, niveauForm);
                showToast("Niveau mis à jour avec succès.");
            } else {
                await API.post('/scolarite/niveaux/creer', niveauForm);
                showToast("Niveau créé avec succès.");
            }
            resetForms();
            fetchData();
        } catch (err) {
            showToast(err.response?.data?.error || "Une erreur est survenue", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleClasseSubmit = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            const payload = { ...classeForm, annee_scolaire_id: classeForm.annee_scolaire_id || selectedAnneeId };
            if (editingId) {
                await API.put(`/scolarite/classes/${editingId}`, payload);
                showToast("Classe mise à jour avec succès.");
            } else {
                await API.post('/scolarite/classes/creer', payload);
                showToast("Classe créée avec succès.");
            }
            resetForms();
            fetchData();
        } catch (err) {
            showToast(err.response?.data?.error || "Une erreur est survenue", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleEditNiveau = (niv) => {
        setEditingId(niv.id);
        setNiveauForm({
            nom_niveau: niv.nom_niveau,
            cycle_id: niv.cycle_id,
            ordre: niv.ordre || 1,
            est_classe_examen: niv.est_classe_examen || 0
        });
        setShowForm(true);
    };

    const handleEditClasse = (cls) => {
        setEditingId(cls.id);
        setClasseForm({
            niveau_id: cls.niveau_id,
            annee_scolaire_id: cls.annee_scolaire_id,
            nom_classe: cls.nom_classe,
            code_classe: cls.code_classe || '',
            capacite: cls.capacite || 35,
            salle: cls.salle || '',
            titulaire_enseignant_id: cls.titulaire_enseignant_id || '',
            est_classe_examen: cls.est_classe_examen || 0,
            type_decoupage_id: cls.type_decoupage_id || ''
        });
        setShowForm(true);
    };

    const askDelete = (id, type, label) => {
        setConfirmAction({
            show: true,
            id,
            type,
            message: `Confirmer la suppression de ${label} ? (Bloqué si des éléments dépendants y sont rattachés).`
        });
    };

    const processConfirm = async () => {
        const { id, type } = confirmAction;
        setConfirmAction({ show: false });
        setActionLoading(true);
        try {
            if (type === 'niveau') {
                await API.delete(`/scolarite/niveaux/${id}`);
                showToast("Niveau supprimé avec succès.");
            } else {
                await API.delete(`/scolarite/classes/${id}`);
                showToast("Classe supprimée avec succès.");
            }
            fetchData();
        } catch (err) {
            showToast(err.response?.data?.error || "Suppression refusée", "error");
        } finally {
            setActionLoading(false);
        }
    };

    return (
        <div style={layoutStyle}>
            <Sidebar />
            <main style={mainStyle}>
                <header style={headerStyle}>
                    <div style={titleContainer}>
                        <div style={iconBoxStyle}><Layers size={22} color="white" /></div>
                        <div>
                            <h1 style={titleStyle}>GESTION PÉDAGOGIQUE</h1>
                            <div style={subtitleStyle}><ShieldCheck size={14} /> Niveaux d'enseignement et Classes</div>
                        </div>
                    </div>
                    <button 
                        onClick={() => {
                            if (showForm) resetForms();
                            else {
                                resetForms();
                                setShowForm(true);
                            }
                        }} 
                        style={showForm ? btnCancel : btnAdd}
                    >
                        {showForm ? <><X size={18} /> ANNULER</> : <><PlusCircle size={18} /> {activeTab === 'niveaux' ? 'NOUVEAU NIVEAU' : 'NOUVELLE CLASSE'}</>}
                    </button>
                </header>

                {/* --- BARRE D'ONGLETS --- */}
                <div style={tabsBarContainer}>
                    <button 
                        onClick={() => { setActiveTab('niveaux'); resetForms(); }}
                        style={activeTab === 'niveaux' ? activeTabBtn : tabBtn}
                    >
                        <Layers size={16} /> Niveaux Pédagogiques ({niveaux.length})
                    </button>
                    <button 
                        onClick={() => { setActiveTab('classes'); resetForms(); }}
                        style={activeTab === 'classes' ? activeTabBtn : tabBtn}
                    >
                        <School size={16} /> Classes ({classes.length})
                    </button>
                    {activeTab === 'classes' && (
                        <div style={{marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px'}}>
                            <span style={{fontSize: '11px', fontWeight: '800', color: '#64748b'}}>Année :</span>
                            <select 
                                style={filterSelectStyle} 
                                value={selectedAnneeId} 
                                onChange={(e) => setSelectedAnneeId(e.target.value)}
                            >
                                <option value="">Toutes les années</option>
                                {annees.map(a => (
                                    <option key={a.id} value={a.id}>{a.libelle}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                {/* --- FORMULAIRE D'AJOUT / MODIFICATION --- */}
                {showForm && (
                    <div style={formWrapper}>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px'}}>
                            <span style={{fontSize: '11px', fontWeight: '800', color: '#2563eb', textTransform: 'uppercase'}}>
                                {editingId ? `Modification (${activeTab === 'niveaux' ? 'Niveau' : 'Classe'})` : `Création (${activeTab === 'niveaux' ? 'Niveau' : 'Classe'})`}
                            </span>
                            <button onClick={resetForms} style={{background: 'none', border: 'none', cursor: 'pointer', color: '#64748b'}}>
                                <X size={16} />
                            </button>
                        </div>
                        {activeTab === 'niveaux' ? (
                            <form onSubmit={handleNiveauSubmit} style={inlineForm}>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>NOM DU NIVEAU (Ex: 6ÈME, SECONDE)</label>
                                    <input 
                                        style={inputStyle} required placeholder="Ex: 6ÈME"
                                        value={niveauForm.nom_niveau} 
                                        onChange={(e) => setNiveauForm({...niveauForm, nom_niveau: e.target.value})} 
                                    />
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>CYCLE PÉDAGOGIQUE</label>
                                    <select 
                                        style={inputStyle} required
                                        value={niveauForm.cycle_id} 
                                        onChange={(e) => setNiveauForm({...niveauForm, cycle_id: e.target.value})}
                                    >
                                        <option value="">Sélectionner un cycle...</option>
                                        {cycles.map(c => (
                                            <option key={c.id} value={c.id}>{c.nom_cycle}</option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{...inputGroup, maxWidth: '100px'}}>
                                    <label style={labelStyle}>ORDRE HIERARCHIQUE</label>
                                    <input 
                                        type="number" min="1" style={inputStyle} required
                                        value={niveauForm.ordre} 
                                        onChange={(e) => setNiveauForm({...niveauForm, ordre: parseInt(e.target.value) || 1})} 
                                    />
                                </div>
                                <button type="submit" disabled={actionLoading} style={btnSubmit}>
                                    {editingId ? "METTRE À JOUR" : "ENREGISTRER"}
                                </button>
                            </form>
                        ) : (
                            <form onSubmit={handleClasseSubmit} style={inlineForm}>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>NIVEAU ASSOCIÉ</label>
                                    <select 
                                        style={inputStyle} required
                                        value={classeForm.niveau_id} 
                                        onChange={(e) => setClasseForm({...classeForm, niveau_id: e.target.value})}
                                    >
                                        <option value="">Sélectionner un niveau...</option>
                                        {niveaux.map(n => (
                                            <option key={n.id} value={n.id}>{n.nom_niveau} ({n.nom_cycle})</option>
                                        ))}
                                    </select>
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>ANNÉE SCOLAIRE</label>
                                    <select 
                                        style={inputStyle} required
                                        value={classeForm.annee_scolaire_id} 
                                        onChange={(e) => setClasseForm({...classeForm, annee_scolaire_id: e.target.value})}
                                    >
                                        <option value="">Sélectionner l'année...</option>
                                        {annees.map(a => (
                                            <option key={a.id} value={a.id}>{a.libelle}</option>
                                        ))}
                                    </select>
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>TYPE DE DÉCOUPAGE</label>
                                    <select 
                                        style={inputStyle} required
                                        value={classeForm.type_decoupage_id} 
                                        onChange={(e) => setClasseForm({...classeForm, type_decoupage_id: e.target.value})}
                                    >
                                        <option value="">Sélectionner le découpage...</option>
                                        {decoupages
                                            .filter(d => !classeForm.annee_scolaire_id || d.annee_scolaire_id === classeForm.annee_scolaire_id)
                                            .map(d => (
                                                <option key={d.id} value={d.id}>{d.libelle} ({d.type_periodicite})</option>
                                            ))}
                                    </select>
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>NOM DE LA CLASSE (Ex: 6ÈME A)</label>
                                    <input 
                                        style={inputStyle} required placeholder="Ex: 6ÈME A"
                                        value={classeForm.nom_classe} 
                                        onChange={(e) => setClasseForm({...classeForm, nom_classe: e.target.value})} 
                                    />
                                </div>
                                <div style={inputGroup}>
                                    <label style={labelStyle}>ENSEIGNANT TITULAIRE</label>
                                    <select 
                                        style={inputStyle}
                                        value={classeForm.titulaire_enseignant_id} 
                                        onChange={(e) => setClasseForm({...classeForm, titulaire_enseignant_id: e.target.value})}
                                    >
                                        <option value="">Sélectionner un titulaire...</option>
                                        {enseignants.map(ens => (
                                            <option key={ens.id} value={ens.id}>{ens.username}</option>
                                        ))}
                                    </select>
                                </div>
                                <div style={{...inputGroup, maxWidth: '90px'}}>
                                    <label style={labelStyle}>CAPACITÉ</label>
                                    <input 
                                        type="number" style={inputStyle} 
                                        value={classeForm.capacite} 
                                        onChange={(e) => setClasseForm({...classeForm, capacite: parseInt(e.target.value) || 0})} 
                                    />
                                </div>
                                <div style={{...inputGroup, maxWidth: '140px'}}>
                                    <label style={labelStyle}>TYPE DE CLASSE</label>
                                    <select 
                                        style={inputStyle}
                                        value={classeForm.est_classe_examen} 
                                        onChange={(e) => setClasseForm({...classeForm, est_classe_examen: parseInt(e.target.value)})}
                                    >
                                        <option value={0}>Passage / Normale</option>
                                        <option value={1}>Examen Officiel</option>
                                    </select>
                                </div>
                                <button type="submit" disabled={actionLoading} style={btnSubmit}>
                                    {editingId ? "METTRE À JOUR" : "ENREGISTRER"}
                                </button>
                            </form>
                        )}
                    </div>
                )}

                {/* --- CONTENU DU TABLEAU SELON L'ONGLET --- */}
                <div style={tableWrapper}>
                    {loading ? (
                        <div style={centerStyle}><Layers className="animate-spin" size={40} color="#2563eb" /></div>
                    ) : (
                        activeTab === 'niveaux' ? (
                            <table style={tableStyle}>
                                <thead>
                                    <tr>
                                        <th style={thStyle}>ID / CODE</th>
                                        <th style={thStyle}>NOM DU NIVEAU</th>
                                        <th style={thStyle}>CYCLE RATTACHÉ</th>
                                        <th style={thStyle}>ORDRE</th>
                                        <th style={thStyle}>ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {niveaux.map(niv => (
                                        <tr key={niv.id} style={trStyle}>
                                            <td style={tdStyle}><code style={codeBadge}>{niv.id}</code></td>
                                            <td style={tdStyle}><strong>{niv.nom_niveau}</strong></td>
                                            <td style={tdStyle}><span style={badgeCycle}>{niv.nom_cycle}</span></td>
                                            <td style={tdStyle}><span style={badgeOrdre}>{niv.ordre}</span></td>
                                            <td style={tdStyle}>
                                                <div style={{display:'flex', gap:'8px'}}>
                                                    <button onClick={() => handleEditNiveau(niv)} style={btnEdit} title="Modifier"><Edit3 size={16} /></button>
                                                    <button onClick={() => askDelete(niv.id, 'niveau', niv.nom_niveau)} style={btnDelete} title="Supprimer"><Trash2 size={16} /></button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {niveaux.length === 0 && (
                                        <tr><td colSpan="5" style={emptyStyle}>Aucun niveau pédagogique enregistré.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                {classes.map(cls => {
                                    const ouvert = Boolean(classesOuvertes[cls.id]);
                                    const matieresClasse = matieresParClasse[cls.id] || [];

                                    return (
                                        <div key={cls.id} style={classeCardStyle}>
                                            <div onClick={() => toggleAccordion(cls.id)} style={classeHeaderStyle}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', flex: 1 }}>
                                                    <span style={chevronBox}>{ouvert ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
                                                    <span style={{ fontSize: '13px', fontWeight: '900', color: '#1e293b' }}>🎓 Classe : {cls.nom_classe}</span>
                                                    <span style={{background: '#eff6ff', color: '#2563eb', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '800'}}>
                                                        {matieresClasse.length} matière(s) configurée(s)
                                                    </span>
                                                    {cls.decoupage_libelle && (
                                                        <span style={{background: '#f1f5f9', color: '#0f172a', padding: '2px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '800', border: '1px solid #cbd5e1'}}>
                                                            Découpage: {cls.decoupage_libelle}
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                                                    <span style={{fontSize: '11px', color: '#64748b', fontWeight: '700'}}>Titulaire : {cls.titulaire_nom || 'Non assigné'}</span>
                                                    <button onClick={(e) => { e.stopPropagation(); handleEditClasse(cls); }} style={btnEdit} title="Modifier la classe"><Edit3 size={15} /></button>
                                                    <button onClick={(e) => { e.stopPropagation(); askDelete(cls.id, 'classe', cls.nom_classe); }} style={btnDelete} title="Supprimer"><Trash2 size={15} /></button>
                                                </div>
                                            </div>

                                            {ouvert && (
                                                <div style={classeBodyStyle}>
                                                    
                                                    {/* ========================================================= */}
                                                    {/* VOLET 1 : Paramétrage des matières et coefficients        */}
                                                    {/* ========================================================= */}
                                                    <div style={voletContainerStyle}>
                                                        <div style={voletTitleStyle}>📦 VOLET 1 : Matières & Coefficients de la classe</div>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px', alignItems: 'end' }}>
                                                            <div>
                                                                <label style={labelStyle}>Matière (Catalogue)</label>
                                                                <select 
                                                                    style={inputStyle}
                                                                    value={selectedMatiereCatalog?.id || ''}
                                                                    onChange={(e) => {
                                                                        const found = matieresCatalogue.find(m => m.id === e.target.value);
                                                                        setSelectedMatiereCatalog(found || null);
                                                                    }}
                                                                >
                                                                    <option value="">-- Choisir une matière --</option>
                                                                    {matieresCatalogue
                                                                        .filter(m => !lignesMatieresClasse.some(l => l.matiere_id === m.id))
                                                                        .map(m => (
                                                                            <option key={m.id} value={m.id}>{m.libelle} ({m.code_matiere})</option>
                                                                        ))}
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label style={labelStyle}>Coefficient</label>
                                                                <input 
                                                                    type="number" step="0.5" min="0.5" style={inputStyle}
                                                                    value={tempCoefficient}
                                                                    onChange={(e) => setTempCoefficient(e.target.value)}
                                                                />
                                                            </div>
                                                            <div>
                                                                <button 
                                                                    type="button" 
                                                                    onClick={handleAddMatiereToClass}
                                                                    style={btnPrimaryStyle}
                                                                >
                                                                    + Ajouter la matière
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {/* Liste des matières ajoutées dans ce volet */}
                                                        <div style={{ marginTop: '12px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                                                                <thead>
                                                                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>
                                                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Code</th>
                                                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Matière</th>
                                                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Coefficient</th>
                                                                        <th style={{ padding: '8px 12px', textAlign: 'left' }}>Professeur Assigné</th>
                                                                        <th style={{ padding: '8px 12px', textAlign: 'center' }}>Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {lignesMatieresClasse.length === 0 ? (
                                                                        <tr>
                                                                            <td colSpan="5" style={{ textAlign: 'center', padding: '15px', color: '#94a3b8', fontStyle: 'italic' }}>
                                                                                Aucune matière définie pour cette classe.
                                                                            </td>
                                                                        </tr>
                                                                    ) : (
                                                                        lignesMatieresClasse.map(l => {
                                                                            const profAssigne = enseignants.find(e => e.id === l.enseignant_id);
                                                                            return (
                                                                                <tr key={l.matiere_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                                                                    <td style={{ padding: '8px 12px' }}><code>{l.code_matiere}</code></td>
                                                                                    <td style={{ padding: '8px 12px', fontWeight: 'bold' }}>{l.libelle}</td>
                                                                                    <td style={{ padding: '8px 12px' }}>Coef {l.coefficient}</td>
                                                                                    <td style={{ padding: '8px 12px' }}>
                                                                                        {profAssigne ? (
                                                                                            <span style={{ color: '#16a34a', fontWeight: 'bold' }}>{profAssigne.username}</span>
                                                                                        ) : (
                                                                                            <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Non assigné</span>
                                                                                        )}
                                                                                    </td>
                                                                                    <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                                                                                        <button 
                                                                                            type="button" 
                                                                                            onClick={() => setLignesMatieresClasse(lignesMatieresClasse.filter(x => x.matiere_id !== l.matiere_id))}
                                                                                            style={{ background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer' }}
                                                                                        >
                                                                                            <Trash2 size={13}/>
                                                                                        </button>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })
                                                                    )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>

                                                    {/* ========================================================= */}
                                                    {/* VOLET 2 : Affectation rigoureuse des professeurs          */}
                                                    {/* ========================================================= */}
                                                    <div style={voletContainerStyle}>
                                                        <div style={voletTitleStyle}>👨‍🏫 VOLET 2 : Affectation des professeurs (Vérification stricte des spécialités)</div>
                                                        
                                                        {lignesMatieresClasse.length === 0 ? (
                                                            <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>
                                                                Veuillez d'abord ajouter des matières dans le Volet 1 pour pouvoir y affecter des professeurs.
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '15px' }}>
                                                                
                                                                {/* Sélection du professeur avec contrôle strict */}
                                                                <div style={{ background: 'white', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                                                    <label style={labelStyle}>Sélectionner un professeur</label>
                                                                    <select 
                                                                        style={inputStyle}
                                                                        value={selectedProf?.id || ''}
                                                                        onChange={(e) => {
                                                                            const found = enseignants.find(ens => ens.id === e.target.value);
                                                                            handleSelectProfesseur(found || null);
                                                                        }}
                                                                    >
                                                                        <option value="">-- Choisir un professeur --</option>
                                                                        {enseignants.map(ens => (
                                                                            <option key={ens.id} value={ens.id}>{ens.username}</option>
                                                                        ))}
                                                                    </select>

                                                                    {selectedProf && (
                                                                        <div style={{ marginTop: '10px', fontSize: '11px', color: '#64748b' }}>
                                                                            Spécialités enregistrées du prof : 
                                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '4px' }}>
                                                                                {(selectedProf.specialites || []).length > 0 ? (
                                                                                    selectedProf.specialites.map(s => (
                                                                                        <span key={s.id || s.matiere_id} style={{ background: '#e0f2fe', color: '#0369a1', padding: '2px 6px', borderRadius: '4px', fontWeight: 'bold' }}>
                                                                                            {s.libelle}
                                                                                        </span>
                                                                                    ))
                                                                                ) : (
                                                                                    <span style={{ color: '#ef4444' }}>Aucune spécialité paramétrée.</span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Affichage des matières de la classe valables pour ce prof */}
                                                                <div style={{ background: 'white', padding: '12px', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                                                    <div>
                                                                        <div style={{ fontSize: '11px', fontWeight: '900', color: '#1e293b', marginBottom: '8px' }}>
                                                                            Cochez les matières de sa spécialité à lui attribuer dans cette classe :
                                                                        </div>
                                                                        {!selectedProf ? (
                                                                            <div style={{ fontSize: '11px', color: '#94a3b8', fontStyle: 'italic' }}>Sélectionnez un professeur valide à gauche.</div>
                                                                        ) : (
                                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '120px', overflowY: 'auto' }}>
                                                                                {lignesMatieresClasse.map(l => {
                                                                                    const profAuraSpecialite = selectedProf.specialites?.some(s => (s.id || s.matiere_id) === l.matiere_id);
                                                                                    const isChecked = !!specialitesProfCochees[l.matiere_id];

                                                                                    return (
                                                                                        <label key={l.matiere_id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', opacity: profAuraSpecialite ? 1 : 0.4, cursor: profAuraSpecialite ? 'pointer' : 'not-allowed' }}>
                                                                                            <input 
                                                                                                type="checkbox"
                                                                                                disabled={!profAuraSpecialite}
                                                                                                checked={isChecked}
                                                                                                onChange={() => handleToggleSpecialiteCoche(l.matiere_id)}
                                                                                            />
                                                                                            <strong>{l.libelle}</strong> 
                                                                                            {!profAuraSpecialite && <span style={{ fontSize: '10px', color: '#ef4444' }}>(Hors spécialité du prof)</span>}
                                                                                        </label>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    <button 
                                                                        type="button"
                                                                        disabled={!selectedProf}
                                                                        onClick={handleApplyProfesseurToClass}
                                                                        style={{ marginTop: '10px', padding: '8px', background: selectedProf ? '#2563eb' : '#cbd5e1', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '800', cursor: selectedProf ? 'pointer' : 'not-allowed', fontSize: '11px' }}
                                                                    >
                                                                        Valider l'affectation pour ce professeur
                                                                    </button>
                                                                </div>

                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Bouton de sauvegarde globale de la classe */}
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '15px' }}>
                                                        <button 
                                                            type="button" 
                                                            disabled={actionLoading}
                                                            onClick={() => handleSaveConfiguration(cls.id)}
                                                            style={{ padding: '10px 20px', background: '#059669', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '800', cursor: 'pointer', fontSize: '12px' }}
                                                        >
                                                            <Save size={14} style={{verticalAlign: 'middle', marginRight: '5px'}}/> ENREGISTRER TOUTE LA CONFIGURATION DE LA CLASSE
                                                        </button>
                                                    </div>

                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {classes.length === 0 && <div style={emptyStyle}>Aucune classe configurée pour cette sélection.</div>}
                            </div>
                        )
                    )}
                </div>

                {/* --- MODALE DE CONFIRMATION --- */}
                {confirmAction.show && (
                    <div style={confirmToastStyle}>
                        <div style={{display:'flex', alignItems:'center', gap:'12px', flex: 1}}>
                            <div style={warnIcon}><AlertTriangle size={20} color="white"/></div>
                            <span style={confirmText}>{confirmAction.message}</span>
                        </div>
                        <div style={{display:'flex', gap:'10px'}}>
                            <button onClick={()=>setConfirmAction({show:false})} style={btnToastCancel}>ANNULER</button>
                            <button onClick={processConfirm} style={btnToastConfirm}>CONFIRMER</button>
                        </div>
                    </div>
                )}

                {/* --- TOAST DE NOTIFICATION --- */}
                {toast.show && (
                    <div style={{...toastStyle, background: toast.type === 'success' ? '#0f172a' : '#dc2626'}}>
                        {toast.type === 'success' ? <CheckCircle2 size={18} color="#10b981" /> : <AlertTriangle size={18} color="white" />}
                        {toast.message}
                    </div>
                )}
            </main>
        </div>
    );
};

// --- STYLES CSS-IN-JS ---
const layoutStyle = { display: 'flex', height: '100vh', background: '#f8fafc', overflow: 'hidden' };
const mainStyle = { flex: 1, display: 'flex', flexDirection: 'column' };
const headerStyle = { background: 'white', padding: '20px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' };
const titleContainer = { display: 'flex', alignItems: 'center', gap: '15px' };
const titleStyle = { margin: 0, fontSize: '18px', fontWeight: '900', color: '#0f172a' };
const subtitleStyle = { fontSize: '12px', color: '#64748b', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' };
const iconBoxStyle = { background: '#2563eb', padding: '10px', borderRadius: '12px' };
const tabsBarContainer = { display: 'flex', alignItems: 'center', background: 'white', padding: '10px 40px', borderBottom: '1px solid #e2e8f0', gap: '10px' };
const tabBtn = { display: 'flex', alignItems: 'center', gap: '8px', background: '#f1f5f9', border: 'none', padding: '10px 20px', borderRadius: '10px', fontSize: '12px', fontWeight: '800', color: '#64748b', cursor: 'pointer' };
const activeTabBtn = { ...tabBtn, background: '#0f172a', color: 'white' };
const filterSelectStyle = { padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '12px', fontWeight: '700' };
const tableWrapper = { padding: '20px 40px', flex: 1, overflowY: 'auto' };
const tableStyle = { width: '100%', borderCollapse: 'separate', borderSpacing: '0 10px' };
const thStyle = { textAlign: 'left', padding: '10px', fontSize: '11px', color: '#64748b', fontWeight: '800', textTransform: 'uppercase' };
const trStyle = { background: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' };
const tdStyle = { padding: '15px 10px', borderTop: '1px solid #f1f5f9', borderBottom: '1px solid #f1f5f9', fontSize: '13px' };
const codeBadge = { background: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', fontSize: '11px' };
const badgeCycle = { background: '#eff6ff', color: '#2563eb', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '800' };
const badgeOrdre = { background: '#f8fafc', color: '#0f172a', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '800', border: '1px solid #cbd5e1' };
const emptyStyle = { textAlign: 'center', padding: '40px', color: '#64748b', fontWeight: '600' };
const btnEdit = { background: '#eff6ff', border: 'none', color: '#2563eb', padding: '8px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const btnDelete = { background: '#fef2f2', border: 'none', color: '#dc2626', padding: '8px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const formWrapper = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.01)' };
const inlineForm = { display: 'flex', alignItems: 'flex-end', gap: '20px', flexWrap: 'wrap' };
const inputGroup = { display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: '180px' };
const labelStyle = { fontSize: '10px', fontWeight: '900', color: '#64748b' };
const inputStyle = { padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontWeight: '700', fontSize: '13px', width: '100%', boxSizing: 'border-box' };
const btnSubmit = { background: '#2563eb', color: 'white', border: 'none', padding: '11px 25px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', height: '41px' };
const btnAdd = { background: '#0f172a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '10px', fontWeight: '800', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' };
const btnCancel = { ...btnAdd, background: '#fee2e2', color: '#dc2626' };
const classeCardStyle = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', marginBottom: '12px' };
const classeHeaderStyle = { background: '#f8fafc', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0' };
const chevronBox = { background: '#e2e8f0', padding: '4px', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#334155' };
const classeBodyStyle = { padding: '20px', background: '#f8fafc' };
const voletContainerStyle = { background: 'white', padding: '15px', borderRadius: '10px', border: '1px solid #e2e8f0', marginBottom: '15px' };
const voletTitleStyle = { fontSize: '12px', fontWeight: '900', color: '#1e293b', marginBottom: '12px' };
const btnPrimaryStyle = { width: '100%', padding: '10px', background: '#2563eb', color: 'white', border: 'none', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' };
const confirmToastStyle = { position: 'fixed', bottom: '30px', left: '50%', transform: 'translateX(-50%)', background: 'white', padding: '15px 25px', borderRadius: '20px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.15)', display: 'flex', alignItems: 'center', minWidth: '500px', zIndex: 9999, border: '2px solid #e2e8f0' };
const warnIcon = { background: '#f59e0b', padding: '8px', borderRadius: '12px' };
const confirmText = { fontWeight: '800', color: '#0f172a', fontSize: '13px' };
const btnToastConfirm = { background: '#0f172a', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '800', cursor: 'pointer' };
const btnToastCancel = { background: '#f1f5f9', color: '#64748b', border: 'none', padding: '10px 20px', borderRadius: '12px', fontWeight: '800', cursor: 'pointer' };
const toastStyle = { position: 'fixed', top: '20px', right: '20px', padding: '15px 30px', borderRadius: '12px', color: 'white', display: 'flex', alignItems: 'center', gap: '12px', zIndex: 9999, fontWeight: '700' };
const centerStyle = { display: 'flex', justifyContent: 'center', padding: '100px' };

export default ClasseNiveau;